import React from 'react';
import {
    Card,
    CardContent,
    Typography,
    Box,
    Divider,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Button,
    TextField,
    InputAdornment,
    Collapse,
    Tooltip,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Remove as RemoveIcon,
    Person as PersonIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Edit as EditPriceIcon,
    History as HistoryIcon,
    CardGiftcard as CardGiftcardIcon,
} from '@mui/icons-material';
import { CustomerOrderHistoryDialog } from '../../../components/orders/CustomerOrderHistoryDialog';
import { CustomerSearchAutocomplete } from '../../../components/common/CustomerSearchAutocomplete';
import { formatCurrencyBDT } from "../../../utils/currency";
import { getCustomerFullName } from "../../../utils/customerUtils";
import { Customer } from '../../../types';
import { CustomerCreationDialog } from '../CustomerCreationDialog';
import { CartItem } from '../types';
import { ComboPriceHint } from '../../../components/orders/ComboPriceHint';
import { ProductThumbnail } from '../../../components/common/ProductThumbnail';
import { KeyHint } from '../../../components/common/KeyHint';
import { SUBMIT_SHORTCUT_LABEL, isSubmitShortcut } from '../../../utils/keyboard';

interface CartProps {
    cart: CartItem[];
    selectedCustomer: Customer | null;
    onCustomerChange: (customer: Customer | null) => void;
    onQuantityUpdate: (productId: string, quantity: number) => void;
    onPriceUpdate: (productId: string, unitPrice: number) => void;
    onRemoveFromCart: (productId: string) => void;
    onClearCart: () => void;
    onCheckout: (deliveryCharge: number, discount: number) => void;
    onCustomerCreate: (customer: Customer) => void;
    total: number;
    loading: boolean;
    initialDeliveryCharge?: number;
    initialDiscount?: number;
    onDeliveryChargeChange?: (charge: number) => void;
    onDiscountChange?: (discount: number) => void;
    /** Called when user picks an item from customer history to add to cart */
    onAddProductFromHistory?: (productId: string, unitPrice: number) => void;
    /** Ctrl/⌘+Enter presses Checkout (desktop POS only — not the phone cart sheet). */
    checkoutShortcut?: boolean;
}

export const Cart: React.FC<CartProps> = ({
    cart,
    selectedCustomer,
    onCustomerChange,
    onQuantityUpdate,
    onPriceUpdate,
    onRemoveFromCart,
    onClearCart,
    onCheckout,
    onCustomerCreate,
    total,
    loading,
    initialDeliveryCharge = 0,
    initialDiscount = 0,
    onDeliveryChargeChange,
    onDiscountChange,
    onAddProductFromHistory,
    checkoutShortcut = false,
}) => {
    const [historyOpen, setHistoryOpen] = React.useState(false);
    const [deliveryCharge, setDeliveryCharge] = React.useState(initialDeliveryCharge);
    const [discount, setDiscount] = React.useState(initialDiscount);
    const [showAdditionalCharges, setShowAdditionalCharges] = React.useState(false);
    const [customerCreationDialogOpen, setCustomerCreationDialogOpen] = React.useState(false);
    const [checkoutWarningOpen, setCheckoutWarningOpen] = React.useState(false);
    // productId -> draft price string while editing; null = not editing
    const [editingPrice, setEditingPrice] = React.useState<{ [productId: string]: string }>({});
    const warningCustomerInputRef = React.useRef<HTMLInputElement>(null);

    // The Checkout button and its shortcut. With no customer attached, ask
    // first (the dialog at the bottom) instead of filing a guest sale silently.
    const requestCheckout = () => {
        if (loading || cart.length === 0) return;
        if (!selectedCustomer) {
            setCheckoutWarningOpen(true);
        } else {
            onCheckout(deliveryCharge, discount);
        }
    };

    // "Continue" in that dialog — also what Enter does there.
    const continueCheckout = () => {
        setCheckoutWarningOpen(false);
        onCheckout(deliveryCharge, discount);
    };

    // Ctrl/⌘+Enter anywhere on the POS presses Checkout — unless a dialog or
    // menu is open on top (payment, quantity, customer…), where keys are its own.
    const requestCheckoutRef = React.useRef(requestCheckout);
    React.useEffect(() => {
        requestCheckoutRef.current = requestCheckout;
    });
    React.useEffect(() => {
        if (!checkoutShortcut) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (!isSubmitShortcut(e)) return;
            if (document.querySelector(".MuiModal-root:not(.MuiModal-hidden)")) return;
            e.preventDefault();
            requestCheckoutRef.current();
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [checkoutShortcut]);

    const handlePriceClick = (productId: string, currentPrice: number) => {
        setEditingPrice((prev) => ({ ...prev, [productId]: String(currentPrice) }));
    };

    const handlePriceChange = (productId: string, value: string) => {
        setEditingPrice((prev) => ({ ...prev, [productId]: value }));
    };

    const commitPrice = (productId: string) => {
        const raw = editingPrice[productId];
        if (raw !== undefined) {
            const parsed = parseFloat(raw);
            if (!isNaN(parsed) && parsed >= 0) {
                onPriceUpdate(productId, parsed);
            }
            setEditingPrice((prev) => {
                const next = { ...prev };
                delete next[productId];
                return next;
            });
        }
    };

    const handlePriceKeyDown = (e: React.KeyboardEvent, productId: string) => {
        if (e.key === "Enter") commitPrice(productId);
        if (e.key === "Escape") {
            setEditingPrice((prev) => {
                const next = { ...prev };
                delete next[productId];
                return next;
            });
        }
    };

    const handleCustomerCreated = (newCustomer: Customer) => {
        onCustomerCreate(newCustomer);
        setCustomerCreationDialogOpen(false);
        // Back in the "No Customer Selected" prompt: return focus to its
        // search box (not the Add Customer button), so Enter continues.
        if (checkoutWarningOpen) {
            setTimeout(() => warningCustomerInputRef.current?.focus(), 0);
        }
    };

    const handleDeliveryChargeChange = (value: string) => {
        const numValue = parseFloat(value) || 0;
        setDeliveryCharge(numValue);
        onDeliveryChargeChange?.(numValue);
    };

    const handleDiscountChange = (value: string) => {
        const numValue = parseFloat(value) || 0;
        setDiscount(numValue);
        onDiscountChange?.(numValue);
    };

    const getFinalTotal = () => {
        return Math.max(total + deliveryCharge - discount, 0);
    };

    return (
        <Card
            sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
            }}
        >
            <CardContent
                sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    minHeight: 0,
                    "&:last-child": { pb: 2 },
                }}
            >
                <Typography variant="h6" gutterBottom>
                    Shopping Cart ({cart.length} items)
                </Typography>

                {/* Customer Selection */}
                <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1 }}>
                        <CustomerSearchAutocomplete
                            value={selectedCustomer}
                            onChange={onCustomerChange}
                            label="Customer (Optional)"
                        />
                    </Box>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<PersonIcon />}
                        onClick={() => setCustomerCreationDialogOpen(true)}
                        sx={{ minWidth: 'fit-content', px: 2 }}
                    >
                        Add Customer
                    </Button>
                    {selectedCustomer && onAddProductFromHistory && (
                        <Tooltip title="View order history">
                            <IconButton
                                size="small"
                                onClick={() => setHistoryOpen(true)}
                                color="primary"
                            >
                                <HistoryIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Cart Items */}
                <Box
                    sx={{
                        flex: 1,
                        overflow: "auto",
                        minHeight: 0,
                    }}
                >
                    {cart.length === 0 ? (
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            textAlign="center"
                            sx={{ py: 4 }}
                        >
                            Cart is empty. Add products to start a sale.
                        </Typography>
                    ) : (
                        <List dense>
                            {cart.map((item) => {
                                const pid = item.product.product_id;
                                const isEditingThisPrice = pid in editingPrice;
                                return (
                                    <ListItem
                                        key={pid}
                                        sx={{ px: 0, alignItems: "flex-start", py: 0.5 }}
                                    >
                                        <ListItemAvatar sx={{ minWidth: 44, mt: 0.5 }}>
                                            <ProductThumbnail
                                                images={item.product.images}
                                                alt={item.product.name}
                                                size={32}
                                            />
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={
                                                <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                    {item.product.name}
                                                    {item.isGift && (
                                                        <Chip
                                                            icon={<CardGiftcardIcon />}
                                                            label="GIFT"
                                                            size="small"
                                                            color="secondary"
                                                            sx={{ height: 18, "& .MuiChip-label": { px: 0.75, fontSize: "0.65rem" } }}
                                                        />
                                                    )}
                                                </Box>
                                            }
                                            secondary={
                                                <>
                                                <Box
                                                    component="span"
                                                    sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.25 }}
                                                >
                                                    {item.isGift ? (
                                                        <Typography variant="body2" component="span" color="secondary.main" fontWeight="medium">
                                                            FREE
                                                        </Typography>
                                                    ) : isEditingThisPrice ? (
                                                        <TextField
                                                            size="small"
                                                            autoFocus
                                                            value={editingPrice[pid]}
                                                            onChange={(e) => handlePriceChange(pid, e.target.value)}
                                                            onBlur={() => commitPrice(pid)}
                                                            onKeyDown={(e) => handlePriceKeyDown(e, pid)}
                                                            type="number"
                                                            inputProps={{ min: 0, step: 0.01, style: { padding: "2px 6px", width: 72 } }}
                                                            InputProps={{
                                                                startAdornment: <InputAdornment position="start" sx={{ mr: 0 }}>৳</InputAdornment>,
                                                            }}
                                                            sx={{ width: 100 }}
                                                        />
                                                    ) : (
                                                        <Tooltip title="Click to edit price">
                                                            <Box
                                                                component="span"
                                                                onClick={() => handlePriceClick(pid, item.unitPrice)}
                                                                sx={{
                                                                    display: "inline-flex",
                                                                    alignItems: "center",
                                                                    gap: 0.25,
                                                                    cursor: "pointer",
                                                                    borderBottom: "1px dashed",
                                                                    borderColor: "text.secondary",
                                                                    "&:hover": { borderColor: "primary.main", color: "primary.main" },
                                                                    transition: "color 0.15s",
                                                                }}
                                                            >
                                                                <Typography variant="body2" component="span">
                                                                    {formatCurrencyBDT(item.unitPrice)}
                                                                </Typography>
                                                                <EditPriceIcon sx={{ fontSize: 11, opacity: 0.5 }} />
                                                            </Box>
                                                        </Tooltip>
                                                    )}
                                                    <Typography variant="body2" component="span" color="text.secondary">
                                                        × {item.quantity} = {item.isGift ? "FREE" : formatCurrencyBDT(item.totalPrice)}
                                                    </Typography>
                                                </Box>
                                                {!item.isGift && (
                                                    <ComboPriceHint
                                                        product={item.product}
                                                        quantity={item.quantity}
                                                        unitPrice={item.unitPrice}
                                                        onUse={(price) => onPriceUpdate(pid, price)}
                                                        sx={{ mt: 0.25 }}
                                                    />
                                                )}
                                                </>
                                            }
                                            primaryTypographyProps={{ variant: "body2", fontWeight: "medium", component: "div" }}
                                        />
                                        <ListItemSecondaryAction>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 0.5,
                                                }}
                                            >
                                                <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                        onQuantityUpdate(
                                                            pid,
                                                            item.quantity - 1
                                                        )
                                                    }
                                                >
                                                    <RemoveIcon fontSize="small" />
                                                </IconButton>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        minWidth: 20,
                                                        textAlign: "center",
                                                    }}
                                                >
                                                    {item.quantity}
                                                </Typography>
                                                <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                        onQuantityUpdate(
                                                            pid,
                                                            item.quantity + 1
                                                        )
                                                    }
                                                >
                                                    <AddIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                        onRemoveFromCart(pid)
                                                    }
                                                    color="error"
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        </ListItemSecondaryAction>
                                    </ListItem>
                                );
                            })}
                        </List>
                    )}
                </Box>

                {/* Total & Actions */}
                {cart.length > 0 && (
                    <Box sx={{ flexShrink: 0 }}>
                        <Divider sx={{ my: 2 }} />

                        {/* Additional Charges Section */}
                        <Box sx={{ mb: 2 }}>
                            <Button
                                onClick={() => setShowAdditionalCharges(!showAdditionalCharges)}
                                startIcon={showAdditionalCharges ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                variant="text"
                                size="small"
                                sx={{ mb: 1, fontSize: '0.875rem' }}
                            >
                                Additional Charges
                            </Button>

                            <Collapse in={showAdditionalCharges}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                                    <TextField
                                        label="Delivery Charge"
                                        type="number"
                                        value={deliveryCharge}
                                        onChange={(e) => handleDeliveryChargeChange(e.target.value)}
                                        size="small"
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start">৳</InputAdornment>,
                                        }}
                                        inputProps={{ min: 0, step: 0.01 }}
                                        helperText="Optional delivery or shipping charge"
                                    />

                                    <TextField
                                        label="Discount"
                                        type="number"
                                        value={discount}
                                        onChange={(e) => handleDiscountChange(e.target.value)}
                                        size="small"
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start">৳</InputAdornment>,
                                        }}
                                        inputProps={{ min: 0, step: 0.01 }}
                                        helperText="Discount amount to apply"
                                    />
                                </Box>
                            </Collapse>

                            {/* Order Summary */}
                            <Box sx={{ mt: 2, p: 2, backgroundColor: 'primary.main', color: 'white', borderRadius: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography variant="body2">Subtotal:</Typography>
                                    <Typography variant="body2">{formatCurrencyBDT(total)}</Typography>
                                </Box>
                                {deliveryCharge > 0 && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                        <Typography variant="body2">Delivery:</Typography>
                                        <Typography variant="body2">+{formatCurrencyBDT(deliveryCharge)}</Typography>
                                    </Box>
                                )}
                                {discount > 0 && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                        <Typography variant="body2">Discount:</Typography>
                                        <Typography variant="body2">-{formatCurrencyBDT(discount)}</Typography>
                                    </Box>
                                )}
                                <Divider sx={{ my: 1, backgroundColor: 'white' }} />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="h6">Total:</Typography>
                                    <Typography variant="h6">{formatCurrencyBDT(getFinalTotal())}</Typography>
                                </Box>
                            </Box>
                        </Box>

                        <Box sx={{ display: "flex", gap: 1 }}>
                            <Button
                                variant="outlined"
                                onClick={onClearCart}
                                sx={{ flex: 1 }}
                            >
                                Clear
                            </Button>
                            <Button
                                variant="contained"
                                onClick={requestCheckout}
                                sx={{ flex: 2 }}
                                disabled={loading}
                                title={checkoutShortcut ? `Checkout (${SUBMIT_SHORTCUT_LABEL})` : undefined}
                            >
                                Checkout
                                {checkoutShortcut && <KeyHint>{SUBMIT_SHORTCUT_LABEL}</KeyHint>}
                            </Button>
                        </Box>
                    </Box>
                )}

                {selectedCustomer && onAddProductFromHistory && (
                    <CustomerOrderHistoryDialog
                        customer={selectedCustomer}
                        open={historyOpen}
                        onClose={() => setHistoryOpen(false)}
                        onAddItem={(productId, _name, _sku, unitPrice) => {
                            onAddProductFromHistory(productId, unitPrice);
                            setHistoryOpen(false);
                        }}
                    />
                )}

                <CustomerCreationDialog
                    open={customerCreationDialogOpen}
                    onClose={() => setCustomerCreationDialogOpen(false)}
                    onCustomerCreated={handleCustomerCreated}
                />

                {/* Catches the common "operator forgot to attach a customer"
                    slip at the moment it matters — checkout — rather than
                    silently filing the sale as a guest order. The picker
                    here is the exact same one above the cart (same props,
                    same state), so fixing the slip never requires leaving
                    this dialog. */}
                <Dialog
                    open={checkoutWarningOpen}
                    onClose={() => setCheckoutWarningOpen(false)}
                    maxWidth="sm"
                    fullWidth
                    slotProps={{ transition: { onEntered: () => warningCustomerInputRef.current?.focus() } }}
                >
                    <DialogTitle>No Customer Selected</DialogTitle>
                    <DialogContent>
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            This sale has no customer attached — it will be recorded as a
                            guest order. If that wasn't intentional, pick or add a customer
                            below before continuing.
                        </Alert>
                        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                            <Box sx={{ flex: 1 }}>
                                <CustomerSearchAutocomplete
                                    value={selectedCustomer}
                                    onChange={onCustomerChange}
                                    label="Customer"
                                    inputRef={warningCustomerInputRef}
                                    autoHighlight
                                    onEnterKey={continueCheckout}
                                />
                            </Box>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<PersonIcon />}
                                onClick={() => setCustomerCreationDialogOpen(true)}
                                sx={{ minWidth: "fit-content", px: 2, mt: "2px" }}
                            >
                                Add Customer
                            </Button>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setCheckoutWarningOpen(false)}>
                            Cancel
                        </Button>
                        <Button variant="contained" onClick={continueCheckout}>
                            {selectedCustomer
                                ? `Continue with ${getCustomerFullName(selectedCustomer)}`
                                : "Continue as Guest"}
                            <KeyHint>Enter</KeyHint>
                        </Button>
                    </DialogActions>
                </Dialog>
            </CardContent>
        </Card>
    );
};
